import { useEffect, useMemo, useRef, lazy, Suspense } from "react";
import { useDispatch } from "react-redux";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { authTriggerActions } from "../../../backend/store/reducers/authReducer";
import { NotFoundLayout, NotFoundInFrame } from "../../../next/shell/NotFoundRoute";
import { LoginPage } from "../../../pages/LoginPage/LoginPage";
import { HouseholdLayout } from "../../CreateHouseholdPage/HouseholdLayout";
import { AuthLayout } from "../AuthLayout/AuthLayout";
import { FrameSwitch } from "../../../next/shell/FrameSwitch";
import { TestPage } from "../../../pages";
import { PrivateRoute } from "../../../hocs/privateRoute";
import { LegacyLayout, LegacyRoute, NextPreviewRoute, SwitchRoute } from "../../../next/routes";
import { MapRoute } from "../../../next/map/MapRoute";
import { RoutesRoute } from "../../../hocs/routesRoute";
import { ReportsRoute } from "../../../next/reports/ReportsRoute";
import { TeamGuard } from "../../../hocs/teamGuard";
import { PermissionRoute } from "../../../hocs/permissionRoute";
import { isPublicNext } from "../../../next/public/mode";
import { takeWantedPage } from "../../../utils/wantedPage";
import { ToWantedOrLanding } from "../../../hocs/toWantedOrLanding";
import { guardFor } from "../../../hocs/guardedPaths";
import { AdminOrSuperAdminRoute } from "../../../hocs/adminOrSuperAdminRoute";
import { useSelector } from "react-redux";
import { WelcomePage } from "../../../pages/WelcomePage";
import { LandingPage } from "../../../pages/LandingPage";
import { LandingVisionPage } from "../../../pages/LandingPage/VisionPage";
import { LandingPartnershipPage } from "../../../pages/LandingPage/PartnershipPage";
import { LandingPlatformPage } from "../../../pages/LandingPage/PlatformPage";
import { LandingTeamPage } from "../../../pages/LandingPage/TeamPage";
import { LandingTestimonialsPage } from "../../../pages/LandingPage/TestimonialsPage";
import { LandingDonatePage } from "../../../pages/LandingPage/DonatePage";
import { LandingContactUsPage } from "../../../pages/LandingPage/ContactUsPage";
import { LegalDocumentsPage } from "../LegalDocuments/LegalDocumentsPage";
// Not launched yet — see the disabled route below.
// import { StoryPage } from "../../../pages/StoryPage";
import { useJsApiLoader } from "@react-google-maps/api";
import { BootLoader } from "../BootLoader/BootLoader";
import { SnackbarMUI } from "../Snackbar";
import { getUser } from "../../../backend/store/actions/creators/getUser";
import { NotificationContext } from "../../../contexts/NotificationContext";
import { useNotifications } from "../../../hooks/useNotifications";
import { AlertNotifications } from "../../AlertNotifications";
import Cookies from "js-cookie";
import { UserContext } from "../../../contexts/UserContext";
import { getDefaultLandingPage } from "../../../utils/permissions";
import { ChabadOrgCallbackPage } from "../../../pages/ChabadOrgCallbackPage";
import { CircularProgress, Box } from "@mui/material";
import { useGlobalRouteOptimizeListener } from "../../../hooks/useGlobalRouteOptimizeListener";
import WelcomeSplash from "../../Onboarding/WelcomeSplash";
import TrialWidget from "../../Onboarding/TrialWidget";
import CountryNudge from "../../Onboarding/CountryNudge";
import { ContactLimitDialog } from "../ContactLimitDialog";
import OverLimitBanner from "../OverLimitBanner";
import { ImpersonationGlow, useImpersonation } from "../Impersonation";
import { useFrameMode, shouldShowGlow } from "../../../next/shell/frameMode";
import { OfflineBanner } from "../OfflineBanner";
import { LegalDocumentsButton } from "../LegalDocuments/LegalDocumentsButton";
import { useConnectivity } from "../../../hooks/useConnectivity";
import { useOfflineSyncEngine } from "../../../offline/useOfflineSyncEngine";
import { PendingLogoutDialog } from "../OfflineBanner/PendingLogoutDialog";
import { ImportProgressProvider } from '../../../contexts/ImportProgressContext';
import { bootGeocodeLocale } from '../../../utils/geocodeLocale';
import { useTranslation } from '../../../hooks/useTranslation';

// Lazy-loaded: only the marketing/landing pages + login need to be in the
// initial bundle a first-time, logged-out visitor downloads. Everything
// behind auth (or admin-only) is fetched on demand instead of bloating
// every visitor's first load with code they may never use.
// DEV ONLY: the new layer's building-block gallery (stage 1A). Never in production builds.
const NextGalleryPage = process.env.NODE_ENV !== "production"
  ? lazy(() => import("../../../next/gallery/GalleryPage"))
  : null;
// DEV ONLY: the Map stage's engine check (20,000 fake pins, for the real-device check). Never in production builds.
const NextMapEngineCheck = process.env.NODE_ENV !== "production"
  ? lazy(() => import("../../../next/gallery/MapEngineCheck"))
  : null;
const VisitReportPage = lazy(() => import("../../../pages/VisitReportPage").then(module => ({ default: module.VisitReportPage })));
// RegistrationPage (the old two-step signup) is no longer routed — /registration now
// serves the redesigned flow. The component is left in the tree rather than deleted, so
// the change is one line to reverse if the new flow has to be pulled in a hurry.
const UpdateCategoriesPage = lazy(() => import("../../../pages/UpdateCategoriesPage").then(module => ({ default: module.UpdateCategoriesPage })));
const ForgotPassword = lazy(() => import("../../../pages/ForgotPasswordPage").then(module => ({ default: module.ForgotPassword })));
const ResetPasswordPage = lazy(() => import("../../../pages/ResetPasswordPage").then(module => ({ default: module.ResetPasswordPage })));
const UnAuthPage = lazy(() => import("../../../pages/UnAuthPage").then(module => ({ default: module.UnAuthPage })));
const AccountSuspendedPage = lazy(() => import("../../../pages/AccountSuspendedPage").then(module => ({ default: module.AccountSuspendedPage })));
const PackagePendingPage = lazy(() => import("../../../pages/PackagePendingPage").then(module => ({ default: module.PackagePendingPage })));
const AdminWorkspacesPage = lazy(() => import("../../../pages/AdminWorkspacesPage").then(module => ({ default: module.AdminWorkspacesPage })));
const AdminUsersPage = lazy(() => import("../../../pages/AdminUsersPage").then(module => ({ default: module.AdminUsersPage })));
const AdminPackagesPage = lazy(() => import("../../../pages/AdminPackagesPage").then(module => ({ default: module.AdminPackagesPage })));
const AdminManagedUploadsPage = lazy(() => import("../../../pages/AdminManagedUploadsPage").then(module => ({ default: module.AdminManagedUploadsPage })));
const AdminSuspectAddressesPage = lazy(() => import("../../../pages/AdminSuspectAddressesPage"));
const AdminPendingUsersPage = lazy(() => import("../../../pages/AdminPendingUsersPage").then(module => ({ default: module.AdminPendingUsersPage })));
const AdminCenterConnectionsPage = lazy(() => import("../../../pages/AdminCenterConnectionsPage").then(module => ({ default: module.AdminCenterConnectionsPage })));
const AdminCentersPage = lazy(() => import("../../../pages/AdminCentersPage").then(module => ({ default: module.AdminCentersPage })));
const SuperAdminDashboardPage = lazy(() => import("../../../pages/SuperAdminDashboardPage").then(module => ({ default: module.SuperAdminDashboardPage })));
const GeocodingShadowPage = lazy(() => import("../../../pages/GeocodingShadowPage").then(module => ({ default: module.GeocodingShadowPage })));
const OnboardingPreviewPage = lazy(() => import("../../../features/OnboardingPreview/OnboardingPreviewPage"));
const AddChabadHousePage = lazy(() => import("../../../pages/AddChabadHousePage").then(module => ({ default: module.AddChabadHousePage })));

const libraries: any = ["places", "drawing", "geometry", "marker"];

// Region bias for the Google Maps JS loader, from the workspace country. Read
// ONCE at module load (before first render) so the loader's options are stable for the
// session — see bootGeocodeLocale(). Null for an unconfigured country → loader unchanged.
const bootLocale = bootGeocodeLocale();

export const App = () => {
  const dispatch = useDispatch();
  // Kept for its subscription: App (and what it passes down) re-renders on a language switch.
  useTranslation();
  const { addNotification, notifications } = useNotifications();
  const isUser = localStorage.getItem("user");
  const frameMode = useFrameMode();
  const viewingAs = useImpersonation().active;

  // <html dir/lang> (RTL for Hebrew) is owned by <I18nGate> (index.tsx): it sets them in the
  // same React commit that renders the words, on the first load and on every switch. A
  // languageChanged listener here flipped the page to RTL a frame before the Hebrew words.

  useEffect(() => {
    // getRoutific()
    if (!Cookies.get("XSRF-TOKEN")) {
      dispatch(authTriggerActions.getTokenTriggerAC());
    }
    // dispatch(householdsTriggerActions.getHouseholdsCategories());
  }, []);

  useEffect(() => {
    if (!isUser) return;
    const urlParams = new URLSearchParams(window.location.search);
    const email = urlParams.get("email");
    const token = urlParams.get("token");
    if (email && token) return;

    // checkOnboarding: true marks this as a genuine sign-in entry point (an
    // existing session landing on the app) for the onboarding redirect in
    // getUser() -- unlike the plain getUser() refreshes SettingsPage and
    // SmartBillingWizard dispatch, which must never navigate anyone away
    // mid-action.
    dispatch(getUser({ checkOnboarding: true }));
  }, [isUser]);

  const navigate = useNavigate();
  const storedCurrentUser = useSelector(
    (state: any) => state.authStore.currentUser
  );
  const currentUser = useMemo(() => storedCurrentUser, [storedCurrentUser]);

  // Track whether the initial landing-page redirect has already fired.
  // This prevents subsequent getUser() polling calls (e.g. from SettingsPage or
  // SmartBillingWizard) from navigating the user away from their current page.
  const hasRedirectedRef = useRef(false);

  // Initialize route notification listeners globally so users receive toast
  // notifications wherever they are in the app.
  useGlobalRouteOptimizeListener(storedCurrentUser?.id);

  // Keeps offlineStore.isOnline in step with the browser's connectivity events.
  useConnectivity();

  // Flushes the offline write queue whenever connectivity returns, and publishes
  // the pending/failed counts on boot so the banner is right after a cold start.
  useOfflineSyncEngine(dispatch, storedCurrentUser?.id);

  useEffect(() => {
    // currentUser is populated by both login sagas and the getUser() refresh saga,
    // so it is available on page refresh (unlike authStore.user which only login sets).
    if (currentUser && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      // Only redirect when on auth pages (login, registration, root).
      // If user is already on an authenticated page (e.g. /saved-routes/123),
      // don't override their current location.
      const authPaths = ["/", "/login", "/registration", "/forgot-password", "/reset-password"];
      const isOnAuthPage = authPaths.includes(window.location.pathname);
      if (!isOnAuthPage) return;
      if (currentUser.isApproved) {
        // Walkthrough M6: this can run before the sign-in page's own redirect; it must honour the page that was wanted too
        // (an e-mailed download link opened signed out), not always the landing page. takeWantedPage is read once.
        navigate((isPublicNext() ? takeWantedPage() : null) ?? getDefaultLandingPage(currentUser));
      } else {
        navigate("/unAuthPage");
      }
    }
  }, [currentUser]);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY as string,
    libraries,
    mapIds: process.env.REACT_APP_GOOGLE_MAP_ID ? [process.env.REACT_APP_GOOGLE_MAP_ID] : [],
    // Country-aware map region bias only (e.g. Israel → "IL"). Spread so an
    // unconfigured country adds NO keys and the loader is byte-identical to before.
    // `language` is intentionally NOT forced here — it's decided per-request from
    // what the user actually typed (see isHebrewText.ts / PlaceAutocompleteInput),
    // since forcing it account-wide overwrote English-typed queries with Hebrew
    // results for Israel workspaces.
    ...(bootLocale?.region ? { region: bootLocale.region } : {}),
  });

  return (
    <UserContext.Provider value={{ user: currentUser, permissions: currentUser?.permissions?.general ?? [], scopedPermissions: currentUser?.permissions?.scoped ?? [], workspaceRole: currentUser?.role }}>
      <NotificationContext.Provider value={{ addNotification, notifications }}>
        {/* Orange glow around the screen while an admin views as a user; the words and
            the way out live in the top bar and side menus (owner, 2026-10-12). */}
        {shouldShowGlow(frameMode, viewingAs) && <ImpersonationGlow />}
        <OfflineBanner />
        <PendingLogoutDialog />
        {/* ImportProgressProvider wraps the WHOLE route tree (not just the
            authenticated SidebarAndAppbarLayout branch it used to sit inside)
            so the same blocking overlay covers a contact import started from
            the /registration routes too — those
            routes sit OUTSIDE the isLoaded-gated authenticated branch below,
            so the provider can't be nested inside that branch anymore. It is
            deliberately NOT gated on `isLoaded` itself: the provider's own
            logic (Pusher/poll/TTL, the overlay Modal) needs nothing from the
            Maps SDK, and gating it would re-exclude the registration routes,
            which never wait on `isLoaded` except for their own city-
            autocomplete step. Every effect inside the provider already
            no-ops when `userId` is undefined (see ImportProgressContext.tsx),
            so it stays inert on the marketing/login pages a logged-out
            visitor sees here too — only one provider must ever be mounted,
            or the overlay/banner would render twice. */}
        <ImportProgressProvider userId={currentUser?.id}>
        <Suspense fallback={<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>}>
        <Routes>
          <Route path="test" element={<TestPage />} />
          {NextGalleryPage && <Route path="next-gallery" element={<NextGalleryPage />} />}
          {NextMapEngineCheck && <Route path="next-gallery/map-engine" element={<NextMapEngineCheck />} />}
          <Route path="/" element={<LandingPage />} />
          <Route path="welcome" element={<WelcomePage />} />
          <Route path="vision" element={<LandingVisionPage />} />
          <Route path="partnership" element={<LandingPartnershipPage />} />
          <Route path="platform" element={<LandingPlatformPage />} />
          <Route path="team" element={<LandingTeamPage />} />
          <Route path="testimonials" element={<LandingTestimonialsPage />} />
          <Route path="donate" element={<LandingDonatePage />} />
          <Route path="contact-us" element={<LandingContactUsPage />} />
          {/* Path matches the old Squarespace .com URL exactly so inbound
              connectyid.com/privacy-policy links redirect 1:1. Shows the current
              Privacy Policy (same text as the Terms & Privacy button). */}
          <Route path="privacy-policy" element={<LegalDocumentsPage />} />

          {/* Aliases for the retired connectyid.com URLs. The .com → .org 301s
              live at the Cloudflare edge (see docs/domain-consolidation-com-to-org.md);
              these are the safety net for anything that reaches .org with an old
              path anyway — a path-preserving domain forward, a mis-scoped redirect
              rule, or a hand-typed link. `replace` keeps them out of history so
              Back doesn't bounce off the alias. */}
          {/* NO alias for /home — that path is the in-app Map page (see the
              authenticated `home` route below). An alias here shadows it and
              throws logged-in users out to the landing page. connectyid.com/home
              is handled by the Cloudflare redirect instead. */}
          <Route path="how-it-works" element={<Navigate to="/platform" replace />} />
          <Route path="donate-page" element={<Navigate to="/donate" replace />} />
          <Route path="register" element={<Navigate to="/registration" replace />} />
          {/* REGISTRATION.
              Deliberately top-level, NOT inside the AuthLayout branch below: the person
              becomes AUTHENTICATED part-way through — the account is created at the
              account step and setup carries on afterwards — and that branch redirects
              authenticated users away, which would eject them mid-signup.

              This used to be /82417/preview/onboarding, an unguessable prefix so only
              the test team could see the redesign while it was being built. That is over
              (owner, 2026-09-24: "we're done with the test, and this needs to be the
              accessible, new registration flow for everyone"), so it now owns
              /registration and the old RegistrationPage is no longer routed. The preview
              path is GONE rather than redirected — it never reached anybody outside the
              test team, so there is nothing out there to keep working.

              TWO separate <Route> entries, not one with an optional `:stepSlug?` param —
              react-router's optional path params only work from 6.5.0, and
              package-lock.json pins react-router-dom to 6.4.2 (this machine's
              node_modules had merely drifted newer, which is why an optional-param route
              once tested green here while quietly 404ing on anything actually built from
              the lockfile — a Docker build installs from it, this dev environment
              doesn't). State surviving the transition between these two relies on React
              reconciling both <Route>s' element as the same component instance at the
              same tree position — true in practice, but an unwritten assumption no test
              covers. Every OnboardingPreview test renders under this same two-route shape
              for that reason. */}
          <Route path="registration" element={<OnboardingPreviewPage />} />
          <Route path="registration/:stepSlug" element={<OnboardingPreviewPage />} />

          <Route
            path="/"
            element={
              currentUser ? (
                currentUser.isApproved ? (
                  <ToWantedOrLanding user={currentUser} />
                ) : (
                  <Navigate to={"/unAuthPage"} />
                )
              ) : (
                <AuthLayout />
              )
            }
          >
            <Route path="login" element={<LoginPage />} />
            {/* No `registration` route here any more — it is top-level now, above.
                It had to move out of this branch: AuthLayout redirects authenticated
                users away, and the new flow signs the person in PART-WAY THROUGH. */}
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route
              path="password-reset/:token"
              element={<ResetPasswordPage />}
            />
          </Route>

          {/* OAuth Callback Route - Public route for OAuth redirects */}
          <Route
            path="oauth/chabadone/callback"
            element={<ChabadOrgCallbackPage />}
          />

          <Route
            path="unAuthPage"
            element={
              <PrivateRoute>
                <UnAuthPage />
              </PrivateRoute>
            }
          />
          {/* The whole authenticated app (dashboard/contacts/map/route/etc.) depends
              on the Google Maps SDK somewhere, so this branch alone still waits for
              it — everything outside it (marketing pages, login) no longer does. */}
          <Route path="/" element={
            // ImportProgressProvider used to wrap SidebarAndAppbarLayout here —
            // it now wraps the whole <Routes> tree above (see the comment at
            // its mount point) so registration is covered too. Only the Maps
            // isLoaded gate remains local to this branch.
            <FrameSwitch mapsReady={isLoaded} bootLoader={<BootLoader />} />
          }>
            <Route
              path="dashboard"
              element={
                <PrivateRoute>
                  {/* Dashboard / Home: the switchboard serves the new Home or today's Dashboard (behind its role guard). */}
                  <SwitchRoute path="/dashboard" />
                </PrivateRoute>
              }
            />
            <Route
              path="map"
              element={
                <PrivateRoute>
                  {/* Stage 4: the switchboard serves the new Map or today's (behind its read_map guard); MapRoute adds the new rule. */}
                  <MapRoute />
                </PrivateRoute>
              }
            />
            {/* Stage 1B-1 Task 10: this page and /duchot, /my-team and
                /saved-routes/:id below take their menu entry's permission
                (hocs/guardedPaths.ts), so nobody can open by address a page
                the menu does not offer him. */}
            <Route
              path="households"
              element={
                <PrivateRoute>
                  <PermissionRoute {...guardFor("/households")}>
                    {/* Old or new page per the switchboard (src/next/routes/switchboard.ts). */}
                    <SwitchRoute path="/households" />
                  </PermissionRoute>
                </PrivateRoute>
              }
            />
            {/* Whole-branch review fix (CRITICAL 1), 2026-09-08: was
                PrivateRoute alone, so a volunteer-only account -- whose entire
                app is /volunteering -- could open the Reports page by URL.
                Gated with AccountRoute, NOT PermissionRoute permission="read_duchot":
                `limited` holds no global permission and never gets read_duchot
                from a scoped grant either (User::generateScopedPermissions only
                ever emits {read,create,update,delete}_{routes,households,
                history,categories,contact_info}), yet a limited user is
                force-granted create_visits and does file reports. See
                hocs/accountRoute.tsx.

                Stage 1B-1 Task 10: now ALSO gated on read_duchot, the Reports
                menu entry's own permission (owner: no way around a missing
                permission). The limited user's reports are filed from the route
                and contact pages, not from this list, and the menu never offered
                him this page. */}
            {/* Stage 8: the switchboard serves the new Reports page or today's. Today's keeps both guards above (they sit in
                bridges/legacyPages.ts LegacyReportsPage); the new page has its own (ReportsRoute: See visit history or Write
                reports, else the "not available" page, which a volunteer reads too). */}
            <Route
              path="duchot"
              element={
                <PrivateRoute>
                  <ReportsRoute />
                </PrivateRoute>
              }
            />

            {/* Whole-branch review fix (CRITICAL 1): same predicate the
                Contacts sidebar entry uses (sideBarRoutes.js: read_households,
                scopeType "households"), which is the only in-app path here --
                ContactsPage's own "New contact" button. Anyone shown that
                entry passes this gate; a volunteer holds neither the
                permission nor any household scope. */}
            <Route
              path="households/create"
              element={
                <PrivateRoute>
                  <PermissionRoute permission="read_households" scopeType="households">
                    {/* Old or new page per the switchboard. */}
                    <SwitchRoute path="/households/create" />
                  </PermissionRoute>
                </PrivateRoute>
              }
            />
            {/* Whole-branch review fix (CRITICAL 1): all three tabs of a
                household take the same Contacts-entry predicate as
                households/create above. Deliberately NOT update_households --
                a `limited` user reaches these tabs on a scoped grant whose
                read/update flags are set independently, and the Contacts page
                that links here is gated on read only. */}
            {/* Today's household tabs around these pages; the new edit page (switchboard "next") renders alone. */}
            <Route path="/" element={<LegacyLayout layout={<HouseholdLayout />} />}>
              <Route
                path="households/update/household-info/:id"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="read_households" scopeType="households">
                      <SwitchRoute path="/households/update/household-info/:id" />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="households/update/categories/:id"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="read_households" scopeType="households">
                      <UpdateCategoriesPage />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="households/update/history/:id"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="read_households" scopeType="households">
                      <VisitReportPage />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />
            </Route>
            {/* The new contact page and its history of changes (stage 2C): same Contacts-entry
                predicate as the routes above. Today's side redirects to the edit tab above. */}
            <Route
              path="households/:id"
              element={
                <PrivateRoute>
                  <PermissionRoute permission="read_households" scopeType="households">
                    <SwitchRoute path="/households/:id" />
                  </PermissionRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="households/:id/changes"
              element={
                <PrivateRoute>
                  <PermissionRoute permission="read_households" scopeType="households">
                    <SwitchRoute path="/households/:id/changes" />
                  </PermissionRoute>
                </PrivateRoute>
              }
            />
            {/* Stage 2F: combine two contacts (the compare). New layer only: today's side redirects like the history. */}
            <Route
              path="households/:id/combine/:otherId"
              element={
                <PrivateRoute>
                  <PermissionRoute permission="read_households" scopeType="households">
                    <SwitchRoute path="/households/:id/combine/:otherId" />
                  </PermissionRoute>
                </PrivateRoute>
              }
            />
            {/* Whole-branch review fix (CRITICAL 1): read_routes with the
                routes scope -- byte-for-byte the predicate BOTH the sidebar
                entry and getDefaultLandingPage already use, so no role loses
                reach (see hocs/permissionRoute.tsx). Walkthrough B1 (2026-10-29): once the
                new page is served, RoutesRoute follows the access model instead. */}
            <Route
              path="saved-routes"
              element={
                <PrivateRoute>
                  <RoutesRoute path="/saved-routes" />
                </PrivateRoute>
              }
            />

            <Route
              path="my-team"
              element={
                <PrivateRoute>
                  <TeamGuard>
                    <SwitchRoute path="/my-team" />
                  </TeamGuard>
                </PrivateRoute>
              }
            />
            {/* 7A: the member page opens over the same list (one component on both addresses). */}
            <Route
              path="my-team/:userId"
              element={
                <PrivateRoute>
                  <TeamGuard>
                    <SwitchRoute path="/my-team/:userId" />
                  </TeamGuard>
                </PrivateRoute>
              }
            />
            {/* 7A F7: the starting sets over the same list (one component with /my-team). */}
            <Route
              path="my-team/starting-sets"
              element={
                <PrivateRoute>
                  <TeamGuard>
                    <SwitchRoute path="/my-team/starting-sets" />
                  </TeamGuard>
                </PrivateRoute>
              }
            />
            {/* Tutorials & New features: the Help page and its tutorials. Anyone signed in may open it (H1.1); what it holds follows what they may do,
                and a locked account sees Contact us only (the page decides; PrivateRoute lets /help through). Off until the switchboard line is flipped. */}
            <Route path="help" element={<PrivateRoute><SwitchRoute path="/help" /></PrivateRoute>} />
            <Route path="help/getting-started" element={<PrivateRoute><SwitchRoute path="/help/getting-started" /></PrivateRoute>} />
            <Route path="help/getting-started/:topicId" element={<PrivateRoute><SwitchRoute path="/help/getting-started/:topicId" /></PrivateRoute>} />
            <Route path="help/whats-new" element={<PrivateRoute><SwitchRoute path="/help/whats-new" /></PrivateRoute>} />
            {/* 7A F6 (board 101 C): the person a hand-over is for answers it here (from the bell or the e-mail). Anyone signed in may open it; the server shows it only to its sender and receiver. */}
            <Route
              path="accept-ownership/:id"
              element={
                <PrivateRoute>
                  <SwitchRoute path="/accept-ownership/:id" />
                </PrivateRoute>
              }
            />
            {/* 5B F4: the route page. Out of today's saved-routes layout (its side panel): the switchboard's legacy side brings that layout back. */}
            <Route
              path="saved-routes/:id"
              element={
                <PrivateRoute>
                  <RoutesRoute path="/saved-routes/:id" />
                </PrivateRoute>
              }
            />

            {/* Billing, Choose a plan and /pricing: the switchboard decides (today's pages keep today's read_billing guard on their legacy side,
                the new page decides by the billing switch itself). Billing page plan. */}
            <Route path="billing" element={<PrivateRoute><SwitchRoute path="/billing" /></PrivateRoute>} />
            <Route path="billing/plans" element={<PrivateRoute><SwitchRoute path="/billing/plans" /></PrivateRoute>} />
            <Route path="pricing" element={<PrivateRoute><SwitchRoute path="/pricing" /></PrivateRoute>} />

            {/* Settings route - open to every authenticated ACCOUNT HOLDER;
                individual sections inside SettingsPage gate themselves by
                role/permission (WorkspaceSettingsCard already needs
                update_settings; Language/Password/Support/Offline are
                per-account and open to everyone).

                Whole-branch review fix (CRITICAL 1), 2026-09-08: it was
                PrivateRoute alone, so a volunteer-only account -- which owns a
                real workspace but is supposed to see nothing of a personal
                account -- could open it and write to it. See
                hocs/accountRoute.tsx for why the guard is the volunteer-only
                shape rather than `update_settings` (which visitor and limited
                do not hold, and which would take away their only way to change
                their own password). */}
            {/* Old or new Settings per the switchboard (today's keeps the AccountRoute guard on its legacy side). */}
            {["settings", "settings/account", "settings/account/addresses", "settings/account/contacts", "settings/categories", "settings/projects", "settings/download"].map((p) => (
              <Route key={p} path={p} element={<PrivateRoute><SwitchRoute path={`/${p}`} /></PrivateRoute>} />
            ))}
            {/* E5.2: the e-mailed download link; sign-in first, then the person's own file (the server checks whose it is). */}
            <Route path="downloads/:token" element={<PrivateRoute><SwitchRoute path="/downloads/:token" /></PrivateRoute>} />
            <Route path="help/contact" element={<PrivateRoute><SwitchRoute path="/help/contact" /></PrivateRoute>} />
            <Route path="admin/messages" element={<PrivateRoute><SwitchRoute path="/admin/messages" /></PrivateRoute>} />
            {/* Walkthrough public #8: the staff pages that exist only in the new layer (Roles and access, Billing settings) open at their own address too. */}
            {["admin/donations", "admin/people", "admin/downloads", "admin/activity", "admin/problems", "admin/route-features", "admin/preview", "admin/audience", "admin/access", "admin/billing/trial", "admin/billing/setup-fee"].map((p) => (
              <Route key={p} path={p} element={<PrivateRoute><SwitchRoute path={`/${p}`} /></PrivateRoute>} />
            ))}

            {/* Old or new Projects page per the switchboard (today's keeps its manage_projects guard on its legacy side). */}
            <Route path="projects" element={<PrivateRoute><SwitchRoute path="/projects" /></PrivateRoute>} />

            {/* Task 12: gated on the permission alone -- an account holder must
                be able to reach this page from the dashboard card before he has
                any volunteer connection at all. The extra condition
                (showsVolunteering) governs only the sidebar entry, never the
                route itself. */}
            <Route
              path="volunteering"
              element={
                <PrivateRoute>
                  <SwitchRoute path="/volunteering" />
                </PrivateRoute>
              }
            />

            {/* Add-a-Chabad-House for an already-registered account
                (add-chabad-house-backend-report.md). Admin/super_admin
                only -- the backend endpoint this screen calls
                (POST /api/centers/connect) is gated the same way
                (ChabadCenterPolicy::connect), and GettingStartedCard's
                "Add your Chabad House" row (the only in-app link here) is
                gated on the same isAdminOrSuperAdmin check, so nothing
                offers this URL to anyone AdminOrSuperAdminRoute would
                bounce anyway -- this is the enforcement, that's just not
                leaving the link lying around for someone to type.

                Fix round 1: uses the dedicated `AdminOrSuperAdminRoute`
                (MAIN-workspace role, matching what the backend policy
                actually checks), NOT the plain `RoleRoute` used just below
                for /admin/dashboard and /admin/geocoding-shadow (CURRENT-
                workspace role) -- see that component's own doc comment for
                why the two questions are different and why this route
                needs the former. */}
            <Route
              path="chabad-house/add"
              element={
                <PrivateRoute>
                  <AdminOrSuperAdminRoute>
                    <AddChabadHousePage />
                  </AdminOrSuperAdminRoute>
                </PrivateRoute>
              }
            />

            {/* Admin Dashboard - staff only: the same check as the rest of the console and the menu (read_admin_workspaces) */}
            <Route
              path="admin/dashboard"
              element={
                <PrivateRoute>
                  <PermissionRoute permission="read_admin_workspaces">
                    <SuperAdminDashboardPage />
                  </PermissionRoute>
                </PrivateRoute>
              }
            />

            {/* Geocoding Shadow Results - staff only, same check */}
            <Route
              path="admin/geocoding-shadow"
              element={
                <PrivateRoute>
                  <PermissionRoute permission="read_admin_workspaces">
                    <GeocodingShadowPage />
                  </PermissionRoute>
                </PrivateRoute>
              }
            />

            {/* Admin routes - Permission-based */}
            <Route
              path="admin/workspaces"
              element={
                <PrivateRoute>
                  <PermissionRoute permission="read_admin_workspaces">
                    <AdminWorkspacesPage />
                  </PermissionRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="admin/users"
              element={
                <PrivateRoute>
                  <PermissionRoute permission="read_admin_users">
                    <AdminUsersPage />
                  </PermissionRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="admin/packages"
              element={
                <PrivateRoute>
                  <PermissionRoute permission="read_admin_packages">
                    <AdminPackagesPage />
                  </PermissionRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="admin/routing/suspect-addresses"
              element={
                <PrivateRoute>
                  <PermissionRoute permission="read_admin_workspaces">
                    <AdminSuspectAddressesPage />
                  </PermissionRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="admin/managed-uploads"
              element={
                <PrivateRoute>
                  <PermissionRoute permission="read_admin_packages">
                    <AdminManagedUploadsPage />
                  </PermissionRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="admin/centers"
              element={
                <PrivateRoute>
                  <PermissionRoute permission="read_admin_users">
                    <AdminCentersPage />
                  </PermissionRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="admin/center-connections"
              element={
                <PrivateRoute>
                  <PermissionRoute permission="read_admin_users">
                    <AdminCenterConnectionsPage />
                  </PermissionRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="admin/pending-users"
              element={
                <PrivateRoute>
                  <PermissionRoute permission="read_admin_users">
                    <AdminPendingUsersPage />
                  </PermissionRoute>
                </PrivateRoute>
              }
            />
            {/* Redesign switchboard (DESIGN.md §3): super-admin-only comparison/preview addresses. */}
            <Route path="legacy/*" element={<PrivateRoute><LegacyRoute /></PrivateRoute>} />
            <Route path="next/*" element={<PrivateRoute><NextPreviewRoute /></PrivateRoute>} />
          </Route>

          {/* Account suspended page - standalone without site layout */}
          <Route
            path="account-suspended"
            element={
              <PrivateRoute>
                <AccountSuspendedPage />
              </PrivateRoute>
            }
          />

          {/* Package pending page - purchased but not yet active */}
          <Route
            path="package-pending"
            element={
              <PrivateRoute>
                <PackagePendingPage />
              </PrivateRoute>
            }
          />

          {/* Not launched yet — page is built but not ready to be public. Uncomment
              (and the import above) to re-enable at /story. */}
          {/* <Route path="story" element={<StoryPage />} /> */}

          {/* Unknown address: a signed-in person sees "Page not found" inside the app frame, a visitor the public page (walkthrough-public #16). */}
          <Route element={<NotFoundLayout mapsReady={isLoaded} bootLoader={<BootLoader />} />}>
            <Route path="*" element={<NotFoundInFrame />} />
          </Route>
        </Routes>
        </Suspense>
        </ImportProgressProvider>
        <WelcomeSplash />
        <TrialWidget />
        {/* Hidden inside the app frame, whose side menu has its own Terms & Privacy row. */}
        <LegalDocumentsButton variant="floating" />
        <OverLimitBanner />
        <CountryNudge />
        <ContactLimitDialog />
        <SnackbarMUI />
        <AlertNotifications notifications={notifications} />
      </NotificationContext.Provider>
    </UserContext.Provider>
  );
};
